import React from "react";
import { ChevronDownIcon, TrendingUpIcon, BarChart3Icon, TrendingUpDownIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Button } from "@mindstonehq/ui";

import { usageTabs, UsageTab } from "./usage-tabs";

type TimePeriod = "3 months" | "6 months" | "12 months";

interface ChartControlsProps {
  activeTab?: UsageTab;
  timePeriod?: TimePeriod;
  chartType?: "Line" | "Bar";
  isCumulative?: boolean;
  onTabChange?: (tab: UsageTab) => void;
  onTimePeriodChange?: (period: TimePeriod) => void;
  onChartTypeChange?: (type: "Line" | "Bar") => void;
  onCumulativeChange?: (cumulative: boolean) => void;
}

export default function ChartControls({
  activeTab = UsageTab.TimeSaved,
  // eslint-disable-next-line lingui/no-unlocalized-strings -- internal Select value identifier
  timePeriod = "6 months",
  // eslint-disable-next-line lingui/no-unlocalized-strings -- internal Select value identifier
  chartType = "Line",
  isCumulative = false,
  onTabChange,
  onTimePeriodChange,
  onChartTypeChange,
  onCumulativeChange,
}: ChartControlsProps) {
  const tabs = usageTabs;

  return (
    <div className="flex flex-row items-end justify-between p-0 w-full">
      {/* Tabs Section */}
      <div className="bg-muted flex flex-row items-start justify-start p-1 rounded-sm shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`flex flex-row h-6 items-center justify-start px-3 py-0 rounded-sm text-xs leading-5 transition-colors ${
              activeTab === tab ? "bg-background text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => onTabChange?.(tab)}
            disabled={!onTabChange} // Disabled for now as requested
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Controls Section */}
      <div className="flex flex-row gap-2 items-center justify-start shrink-0">
        {/* Time Period Dropdown */}
        <Select value={timePeriod} onValueChange={onTimePeriodChange} disabled={!onTimePeriodChange}>
          <SelectTrigger className="bg-background h-8 rounded-sm border border-border flex flex-row gap-2 items-center justify-start px-3 py-0 hover:bg-muted transition-colors w-auto">
            <SelectValue>
              <span className="text-xs leading-5 text-foreground">{timePeriod}</span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3 months">
              <span className="text-xs">3 months</span>
            </SelectItem>
            <SelectItem value="6 months">
              <span className="text-xs">6 months</span>
            </SelectItem>
            <SelectItem value="12 months">
              <span className="text-xs">12 months</span>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Chart Type Dropdown */}
        <Select value={chartType} onValueChange={onChartTypeChange} disabled={!onChartTypeChange}>
          <SelectTrigger className="bg-background h-8 rounded-sm border border-border flex flex-row gap-2 items-center justify-start px-3 py-0 hover:bg-muted transition-colors w-auto">
            {chartType === "Line" ? (
              <TrendingUpIcon className="size-4 text-muted-foreground" />
            ) : (
              <BarChart3Icon className="size-4 text-muted-foreground" />
            )}
            <SelectValue>
              <span className="text-xs leading-5 text-foreground">{chartType}</span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Line">
              <div className="flex items-center gap-2">
                <TrendingUpIcon className="size-4 text-muted-foreground" />
                <span className="text-xs">Line</span>
              </div>
            </SelectItem>
            <SelectItem value="Bar">
              <div className="flex items-center gap-2">
                <BarChart3Icon className="size-4 text-muted-foreground" />
                <span className="text-xs">Bar</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Cumulative Toggle  if we ever want to use it*/}
        {/* <Button
          variant={isCumulative ? "default" : "outline"}
          size="sm"
          className="h-8 px-3"
          onClick={() => onCumulativeChange?.(!isCumulative)}
          disabled={!onCumulativeChange}
        >
          <TrendingUpDownIcon className="size-4 mr-2" />
          <span className="text-xs">Cumulative</span>
        </Button> */}
      </div>
    </div>
  );
}
